// HubDrillGrid — middle layer between TileGrid and ItemGrid. Mirrors
// the wizard's step-2 hub→leaf pattern (ShopModePlacementWizard.jsx
// ~lines 491-598): pantry items inside a tile that share a hub
// canonical (chicken_hub, beef_hub, cheese_hub, milk_hub, …) cluster
// under a hub chip; tapping the chip drills into that hub's items.
// Items with no hub parent render as KitchenCards inline below.

import { memo } from "react";
import { GlassPanel, withAlpha } from "./primitives";
import { useTheme } from "./theme";
import { font } from "./tokens";
import { ItemGrid } from "./ItemGrid";
import { bundledIconFor } from "../../lib/canonicalIcons";

export function HubDrillGrid({
  hubs,
  orphans,
  onPickHub,
  onOpenItem,
  onOpenUnitPicker,
  onUpdateItem,
  onDeleteItem,
  onRestockItem,
  onOpenStack = null,
  brandSuggestions = [],
}) {
  return (
    <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 18 }}>
      {hubs.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 10,
        }}>
          {hubs.map(hub => (
            <HubChip key={hub.id} hub={hub} onPick={onPickHub} />
          ))}
        </div>
      )}
      {orphans.length > 0 && (
        <ItemGrid
          items={orphans}
          onOpenItem={onOpenItem}
          onOpenUnitPicker={onOpenUnitPicker}
          onUpdateItem={onUpdateItem}
          onDeleteItem={onDeleteItem}
          onRestockItem={onRestockItem}
          onOpenStack={onOpenStack}
          brandSuggestions={brandSuggestions}
        />
      )}
    </div>
  );
}

function HubChipImpl({ hub, onPick }) {
  const { theme } = useTheme();
  // Hub ids end with "_hub" (chicken_hub, beef_hub). Bundled SVG
  // icons are keyed by the bare canonical slug (chicken.svg), so
  // strip the suffix before lookup. Falls back to emoji for hubs
  // without a curated icon.
  const iconUrl = bundledIconFor(hub.id.replace(/_hub$/, ""));
  return (
    <GlassPanel
      flat
      interactive
      onClick={() => onPick(hub.id)}
      padding={12}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        minHeight: 72,
        cursor: "pointer",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        background: theme.color.glassFillLite,
      }}
    >
      <div style={{ position: "relative", width: 40, height: 40, flexShrink: 0 }}>
        {iconUrl ? (
          <img
            src={iconUrl}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : (
          <div style={{ fontSize: 32, lineHeight: 1 }}>{hub.emoji || "•"}</div>
        )}
        <div
          title={`${hub.childCount} item${hub.childCount === 1 ? "" : "s"}`}
          style={{
            position: "absolute",
            top: -2, right: -4,
            minWidth: 18, height: 18,
            padding: "0 5px",
            borderRadius: 999,
            background: theme.color.teal,
            color: theme.color.ctaText,
            fontFamily: font.mono, fontSize: 10, fontWeight: 600,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            letterSpacing: "-0.02em",
            boxShadow: `0 2px 6px ${withAlpha(theme.color.teal, 0.40)}`,
            border: `1px solid ${theme.color.glassBorder}`,
          }}
        >
          {hub.childCount}
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{
          display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8,
        }}>
          <div style={{
            fontFamily: font.display,
            fontWeight: 500,
            fontSize: 18, lineHeight: 1.1, color: theme.color.ink,
            letterSpacing: "0.02em",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            flex: 1, minWidth: 0,
          }}>
            {hub.name}
          </div>
          {hub.warnCount > 0 && (
            <div
              className="mcm-warn-pill"
              title={`${hub.warnCount} expiring soon`}
              style={{
                minWidth: 20, height: 20,
                padding: "0 7px",
                borderRadius: 999,
                background: theme.color.burnt,
                color: theme.color.ctaText,
                fontFamily: font.mono, fontSize: 9, fontWeight: 600,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                flexShrink: 0,
                border: `1px solid ${theme.color.glassBorder}`,
                boxShadow: "0 2px 8px rgba(168,73,17,0.40)",
              }}
            >
              {hub.warnCount} soon
            </div>
          )}
        </div>
        <div style={{
          fontFamily: font.mono, fontSize: 10,
          color: theme.color.inkFaint,
          letterSpacing: "0.10em", textTransform: "uppercase",
        }}>
          {hub.childCount} {hub.childCount === 1 ? "kind" : "kinds"} →
        </div>
      </div>
    </GlassPanel>
  );
}

const HubChip = memo(HubChipImpl);
